import type { MaybeRefOrGetter } from 'vue'
import type { CVData } from '~/types'
import { liveMatchScore, type TailorPreview, type TailorSkillLine } from '~/utils/cvTailor'
import { isPostingUrl } from '~/utils/postingUrl'

/**
 * "Paste the posting, get the CV rewritten for it in seconds", under a CV
 * just built: the state behind LandingTailorDemo.
 *
 * One instance per result, shared by the card on the page and the panel that
 * opens after the free download, so a visitor who already ran it sees the
 * same result there instead of being asked again (the free run is once a day).
 * The posting is parked for the editor, which tailors to it as soon as the
 * pack is paid for. The field also takes the ad's link (usePostingFromUrl).
 */
export interface TailorDemoOptions {
  cv: MaybeRefOrGetter<CVData>
  /** The layout on screen; a tailored PDF parked for sale starts in it. */
  template?: MaybeRefOrGetter<string | undefined>
  /** `guest_cv_generations` id, so the run shows up on the visitor's journey. */
  generationId?: MaybeRefOrGetter<string | null | undefined>
  /** Extra fields for the journey event, e.g. the result-template experiment. */
  trackingData?: MaybeRefOrGetter<Record<string, unknown> | undefined>
}

/** A posting shorter than this is a title or a link, not something to tailor to. */
export const TAILOR_DEMO_MIN_POSTING = 80
export const TAILOR_DEMO_MAX_POSTING = 8000
/** What the AI does, in the order the waiting screen ticks it off. */
export const TAILOR_DEMO_STEPS = ['read', 'compare', 'profile', 'experience'] as const
const STEP_MS = 1800
/**
 * Below this the tailoring is not celebrated: the CV does not show what the
 * posting asks for, and the result says so instead of showing a "+4 %".
 */
export const TAILOR_LOW_MATCH = 30

export function useTailorDemo(options: TailorDemoOptions) {
  const { t, locale } = useI18n()
  const { setPendingPosting } = usePendingPurchase()
  const journey = useJourney()

  const posting = ref('')
  const isRunning = ref(false)
  const message = ref<string | null>(null)
  /** Today's free run is used: shown as its own notice, with the pack to buy. */
  const limitReached = ref(false)
  const preview = ref<TailorPreview | null>(null)
  /** Seconds since the run started, ticking while it runs. */
  const elapsed = ref(0)
  /** How long the finished run took, for "rewritten in 6.2 s". */
  const tookSeconds = ref<number | null>(null)
  /** Index into TAILOR_DEMO_STEPS of the step being "worked on". */
  const step = ref(0)
  /** Skills from the posting the visitor ticked as theirs; the tailored PDF lists them. */
  const confirmed = ref<string[]>([])
  /** For ticked skills, where the visitor says they used them: lines the tailored PDF adds under those roles. */
  const skillLines = ref<TailorSkillLine[]>([])
  /**
   * Free preview: the locked changes the visitor added to the CV, by their
   * place in `preview.locked`. Their text stays locked; adding one only counts
   * it in the match. The PDF bought has every change either way, each one to
   * keep or undo in the editor afterwards.
   */
  const added = ref<number[]>([])
  /** Everything counts as added where nothing is locked (a plan that shows the text). */
  const addedCount = computed(() => (preview.value?.limited ? added.value.length : preview.value?.locked.length ?? 0))
  /** The match with the changes added and the ticked skills. */
  const liveScore = computed(() => (preview.value ? liveMatchScore(preview.value, { added: addedCount.value, ticked: confirmed.value.length }) : 0))
  /** The same with every change added: what the tailoring brings, for the lines around the card that sell it. */
  const potentialScore = computed(() => (preview.value ? liveMatchScore(preview.value, { added: preview.value.locked.length, ticked: confirmed.value.length }) : 0))
  const lowMatch = computed(() => !!preview.value && liveScore.value < TAILOR_LOW_MATCH)
  let confirmTracked = false
  let addTracked = false

  function clearChoices() {
    confirmed.value = []
    skillLines.value = []
    added.value = []
    confirmTracked = false
    addTracked = false
  }

  const tooShort = computed(() => posting.value.trim().length < TAILOR_DEMO_MIN_POSTING)
  const fromUrl = usePostingFromUrl(posting, { max: TAILOR_DEMO_MAX_POSTING })
  /** A link that is still loading, or could not be read: its message says what to do. */
  const postingIsLink = computed(() => isPostingUrl(posting.value))

  let timer: ReturnType<typeof setInterval> | null = null
  function stopTimer() {
    if (timer) clearInterval(timer)
    timer = null
  }

  async function run(where: 'card' | 'after_download') {
    if (isRunning.value || postingIsLink.value) return
    if (tooShort.value) {
      message.value = t('landing.builder.jobMatch.tooShort')
      return
    }

    const text = posting.value.trim().slice(0, TAILOR_DEMO_MAX_POSTING)
    isRunning.value = true
    clearChoices()
    message.value = null
    limitReached.value = false
    elapsed.value = 0
    step.value = 0
    const started = performance.now()
    timer = setInterval(() => {
      const ms = performance.now() - started
      elapsed.value = ms / 1000
      // The last step stays "in progress" until the answer is in.
      step.value = Math.min(TAILOR_DEMO_STEPS.length - 1, Math.floor(ms / STEP_MS))
    }, 100)

    let status = 0
    try {
      preview.value = await $fetch<TailorPreview>('/api/ai/tailor-preview', {
        method: 'POST',
        body: {
          cvData: toValue(options.cv),
          jobDescription: text,
          template: toValue(options.template),
          locale: locale.value,
          generationId: toValue(options.generationId) ?? undefined,
          visitorId: journey.visitorId() ?? undefined,
        },
      })
      tookSeconds.value = Math.round((performance.now() - started) / 100) / 10
      setPendingPosting(text)
    } catch (err) {
      status = (err as { statusCode?: number; response?: { status?: number } }).statusCode
        ?? (err as { response?: { status?: number } }).response?.status
        ?? 0
      if (status === 429) limitReached.value = true
      else message.value = t('landing.builder.jobMatch.error')
    } finally {
      stopTimer()
      isRunning.value = false
    }

    const result = preview.value
    journey.track('cv_job_match', {
      generation_id: toValue(options.generationId) ?? null,
      where,
      ok: !status && !!result,
      ...(result && !status
        ? { score: result.matchScore, score_after: result.matchScoreAfter, score_confirmed: result.matchScoreConfirmed, low: lowMatch.value, changes: result.locked.length + (result.sample ? 1 : 0), seconds: tookSeconds.value, limited: result.limited }
        : { status }),
      ...toValue(options.trackingData),
    })
  }

  /** The textarea's value; a method, so the component that shows it does not write into its prop. */
  function setPosting(value: string) {
    posting.value = value
  }

  /** Adds a locked change to the CV, or takes it out; `index` is its place in `preview.locked`. */
  function toggleAdded(index: number) {
    added.value = added.value.includes(index) ? added.value.filter(item => item !== index) : [...added.value, index]
    trackAdded()
  }

  /** Every locked change at once. */
  function addAll() {
    added.value = (preview.value?.locked ?? []).map((_, index) => index)
    trackAdded()
  }

  // Once per result: whether the visitor builds the CV up at all, not every click.
  function trackAdded() {
    if (addTracked || !added.value.length) return
    addTracked = true
    journey.track('cv_job_match_add', {
      generation_id: toValue(options.generationId) ?? null,
      offered: preview.value?.locked.length ?? 0,
      score_after: preview.value?.matchScoreAfter ?? null,
      ...toValue(options.trackingData),
    })
  }

  /** The lines written for ticked skills; a method, so the component that shows them does not write into its prop. */
  function setSkillLines(lines: TailorSkillLine[]) {
    const before = skillLines.value.length
    skillLines.value = lines.filter(item => confirmed.value.includes(item.skill))
    // Written or taken out, not every rewording.
    if (skillLines.value.length === before) return
    journey.track('cv_job_match_skill_line', {
      generation_id: toValue(options.generationId) ?? null,
      lines: skillLines.value.length,
      ...toValue(options.trackingData),
    })
  }

  /** Ticks or unticks a skill from the posting as the visitor's own. */
  function toggleConfirmed(skill: string) {
    confirmed.value = confirmed.value.includes(skill)
      ? confirmed.value.filter(item => item !== skill)
      : [...confirmed.value, skill]
    // An unticked skill takes its line with it.
    skillLines.value = skillLines.value.filter(item => confirmed.value.includes(item.skill))
    // Once per result: whether the question is answered at all, not every tick.
    if (!confirmTracked && confirmed.value.length) {
      confirmTracked = true
      journey.track('cv_job_match_confirm', {
        generation_id: toValue(options.generationId) ?? null,
        offered: preview.value?.confirmSkills.length ?? 0,
        score_after: preview.value?.matchScoreAfter ?? null,
        ...toValue(options.trackingData),
      })
    }
  }

  /**
   * A tailoring the visitor already ran, shown again as it was (the day-0
   * e-mail's link, /api/guest-cv/<id>/tailoring): no new AI run, so the day's
   * free run is not asked for twice.
   */
  function restore(saved: TailorPreview, savedPosting: string | null) {
    preview.value = saved
    posting.value = savedPosting ?? ''
    clearChoices()
    message.value = null
    limitReached.value = false
    tookSeconds.value = null
  }

  /** Back to the empty posting, e.g. to try another one. */
  function reset() {
    preview.value = null
    clearChoices()
    posting.value = ''
    message.value = null
    limitReached.value = false
    tookSeconds.value = null
  }

  onBeforeUnmount(stopTimer)

  return {
    posting,
    isRunning,
    message,
    limitReached,
    preview,
    elapsed,
    tookSeconds,
    step,
    confirmed,
    skillLines,
    added,
    liveScore,
    potentialScore,
    lowMatch,
    tooShort,
    postingIsLink,
    postingUrlLoading: fromUrl.isLoading,
    postingUrlError: fromUrl.error,
    postingLoadedFrom: fromUrl.loadedFrom,
    run,
    setPosting,
    toggleConfirmed,
    setSkillLines,
    toggleAdded,
    addAll,
    restore,
    reset,
  }
}

export type TailorDemo = ReturnType<typeof useTailorDemo>
